import React from "react";
import { Users, Star, Search, Filter, Repeat, Database } from "lucide-react";
import FeaturePage, { MockWindow } from "../features/FeaturePage";
import { siblingSolutions, DEMO_CTA } from "./links";

const ACCENT = "#059669";

const STAGES = [
  { label: "Applied", n: 128, tone: "#94A3B8" },
  { label: "Shortlisted", n: 34, tone: "#0025D8" },
  { label: "Interviewed", n: 18, tone: "#7C3AED" },
  { label: "Talent pool", n: 22, tone: ACCENT },
];

// ── Hero visual: pipeline stages ──
function PipelineVisual() {
  const max = Math.max(...STAGES.map((s) => s.n));
  return (
    <MockWindow label="PIPELINE · PRODUCT MANAGER" accent={ACCENT}>
      <div className="space-y-3">
        {STAGES.map((s) => (
          <div key={s.label}>
            <div className="flex items-center justify-between text-[11px] mb-1">
              <span className="text-muted-foreground">{s.label}</span>
              <span className="font-mono font-semibold" style={{ color: s.tone }}>{s.n}</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: `${s.tone}20` }}>
              <div className="h-2 rounded-full" style={{ width: `${(s.n / max) * 100}%`, background: s.tone }} />
            </div>
          </div>
        ))}
      </div>
    </MockWindow>
  );
}

// ── Row visual: talent pool cards ──
function PoolVisual() {
  const people = [
    { name: "P. Nomvete", role: "Data Analyst", tag: "Strong · past role" },
    { name: "L. Haddad", role: "Ops Manager", tag: "Borderline · reachable" },
    { name: "D. Whitfield", role: "UX Designer", tag: "Strong · 2 roles ago" },
  ];
  return (
    <MockWindow label="TALENT POOL" badge="22 saved" accent={ACCENT}>
      <div className="space-y-2.5">
        {people.map((p) => (
          <div key={p.name} className="flex items-center gap-3 rounded-xl border border-border p-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold" style={{ background: ACCENT }}>
              {p.name[0]}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm text-[#0F172A]">{p.name}</div>
              <div className="text-[11px] text-muted-foreground">{p.role} · {p.tag}</div>
            </div>
            <Star className="w-4 h-4 flex-shrink-0" style={{ color: ACCENT }} fill={ACCENT} />
          </div>
        ))}
      </div>
    </MockWindow>
  );
}

// ── Row visual: reuse for a new role ──
function ReuseVisual() {
  return (
    <MockWindow label="NEW ROLE · SUGGESTED FROM POOL" accent={ACCENT}>
      <div className="space-y-3">
        <div className="rounded-xl border border-border p-3 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Marketing Manager — Growth</span>
        </div>
        <div className="flex items-center justify-center gap-1.5 font-mono text-[10px]" style={{ color: ACCENT }}>
          <Repeat className="w-3.5 h-3.5" /> 5 past candidates match this role
        </div>
        <div className="space-y-2">
          {["D. Whitfield — 92% match", "P. Nomvete — 84% match", "L. Haddad — 71% match"].map((t, i) => (
            <div key={i} className="flex items-center justify-between rounded-xl border p-2.5"
              style={{ borderColor: `${ACCENT}26`, background: `${ACCENT}0A` }}>
              <span className="text-xs text-[#0F172A]">{t.split(" — ")[0]}</span>
              <span className="font-mono text-[10px] font-bold" style={{ color: ACCENT }}>{t.split(" — ")[1]}</span>
            </div>
          ))}
        </div>
      </div>
    </MockWindow>
  );
}

export default function TalentPoolSolution() {
  return (
    <FeaturePage
      accent={ACCENT}
      signupRole="company"
      secondaryCta={DEMO_CTA}
      eyebrow="TALENT POOL & PIPELINE"
      title="Great candidates"
      titleAccent="don't expire."
      subtitle="Track every applicant through a clear pipeline, and keep the strong ones you didn't hire in a reusable talent pool — so your next opening starts with a warm shortlist instead of a blank job board."
      heroChips={["Clear hiring pipeline", "Reusable talent pool", "Warm-start every role"]}
      heroVisual={<PipelineVisual />}
      rows={[
        {
          tag: "PIPELINE",
          title: "See where every candidate stands.",
          body: "From applied to shortlisted to interviewed, your whole pipeline is visible at a glance for each role. No spreadsheets, no lost candidates, no wondering who's waiting on you.",
          bullets: [
            "Stage-by-stage view per role",
            "Shortlist and advance in a click",
            "Nothing falls through the cracks",
          ],
          visual: <PipelineVisual />,
        },
        {
          tag: "TALENT POOL",
          title: "Keep the ones that got away.",
          body: "A strong candidate you couldn't place today is your fastest hire tomorrow. Save them to a talent pool with their score and context intact, so you can come back when the right role opens.",
          bullets: [
            "Save strong candidates for later",
            "Scores and context kept with them",
            "Consent-aware retention built in",
          ],
          visual: <PoolVisual />,
          reverse: true,
        },
        {
          tag: "REUSE",
          title: "Start your next role half-done.",
          body: "When you open a similar position, surface matching people from your pool instead of sourcing from zero. Re-invite them to a fresh interview in a click and skip the cold-start entirely.",
          bullets: [
            "Match past candidates to new roles",
            "Re-invite in a single click",
            "Warm shortlist from day one",
          ],
          visual: <ReuseVisual />,
        },
      ]}
      steps={[
        { title: "Run your hiring", body: "Candidates flow through a clear pipeline for each role." },
        { title: "Save the strong ones", body: "Add promising candidates you didn't hire to your talent pool." },
        { title: "Reuse next time", body: "Surface and re-invite matching people when a new role opens." },
      ]}
      useCases={[
        { icon: Repeat, persona: "Recurring roles", body: "Hire the same profiles often? Your pool means every round starts warm." },
        { icon: Database, persona: "Agencies & consultants", body: "Build a reusable bench of vetted candidates across all your clients and roles." },
        { icon: Filter, persona: "Lean teams", body: "Stop re-sourcing the same talent — keep good people one click away." },
      ]}
      faqs={[
        { q: "What is the talent pool?", a: "It's where you keep strong candidates you didn't hire, with their interview score and context preserved, so you can re-engage them when a suitable role opens instead of sourcing from scratch." },
        { q: "How does re-using a candidate work?", a: "When you create a similar role, matching people from your pool are surfaced and you can re-invite them to a fresh interview in one click." },
        { q: "Is keeping candidate data compliant?", a: "Retention is consent-aware and tied to your data-retention settings, so candidates stay in the pool only as long as your policy and their consent allow. See the Compliance solution for details." },
        { q: "Can I see my whole pipeline per role?", a: "Yes — each role shows a stage-by-stage view from applied through interviewed, so you always know who's where." },
        { q: "Does this work for agencies?", a: "Especially well — a reusable, vetted bench of candidates across clients and roles is exactly what the talent pool is built for." },
      ]}
      ctaTitle="Never start from zero again."
      ctaSubtitle="Start free, or book a demo to see the pipeline on your roles."
      related={siblingSolutions("talent-pool")}
      seoTitle="Talent Pool & Hiring Pipeline — Reuse Great Candidates | VetraHub"
      seoDescription="Track applicants through a clear pipeline and keep strong candidates you didn't hire in a reusable, consent-aware talent pool. Warm-start every new role."
    />
  );
}
